<script setup>
import { reactive, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import SipChannelAPI from '../api/sipChannel';
import { useSipValidation } from '../composables/useSipValidation';

import PageHeader from 'dashboard/routes/dashboard/settings/SettingsSubPageHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Select from 'v3/components/Form/Select.vue';

const DEFAULT_WS_PORT = 8088;
const DEFAULT_WSS_PORT = 8089;

onMounted(() => {
  console.log('[PLUGIN][SIP-CHANNEL] SIP component mounted');
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();

// Custom validators
const validSipServer = helpers.withMessage(
  'Must be a valid hostname or IP address',
  value => {
    if (!value) return true;
    // Hostname or IP validation
    const hostnameRegex =
      /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/i;
    const ipv4Regex = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/;
    return hostnameRegex.test(value) || ipv4Regex.test(value);
  }
);

const validPort = helpers.withMessage(
  'Port must be between 1 and 65535, or empty for default',
  value => {
    if (!value || value === '') return true; // Permite vazio para usar porta padrão
    const port = parseInt(value, 10);
    return port >= 1 && port <= 65535;
  }
);

const state = reactive({
  name: '',
  sipServer: '',
  sipPort: '', // Vazio = usa porta padrão (8089 para WSS, 8088 para WS)
  sipTransport: 'wss',
  sipRealm: '',
  registrationMode: 'per_agent', // 'shared' ou 'per_agent'
});

const getDefaultSipPort = transport => {
  return transport === 'wss' ? String(DEFAULT_WSS_PORT) : String(DEFAULT_WS_PORT);
};

const uiFlags = useMapGetter('inboxes/getUIFlags');
const {
  isValidating,
  validationStatus,
  validationMessage,
  lastValidationTime,
  validateSipConnection,
} = useSipValidation();

const formattedValidationTime = computed(() => {
  if (!lastValidationTime.value) return '';
  const date = new Date(lastValidationTime.value);
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
});

const validationTimeLabel = computed(() => {
  return formattedValidationTime.value
    ? `(${formattedValidationTime.value})`
    : '';
});

const registrationModeOptions = [
  {
    label: 'Registro por Agente (cada agente tem seu ramal)',
    value: 'per_agent',
  },
  {
    label: 'Único Registro Compartilhado (todos agentes usam o mesmo ramal)',
    value: 'shared',
  },
];

const registrationModeLabel = computed(() => {
  return state.registrationMode === 'per_agent'
    ? t('INBOX_MGMT.ADD.VOICE.SIP_FORM.REGISTRATION.PER_AGENT_TITLE')
    : t('INBOX_MGMT.ADD.VOICE.SIP_FORM.REGISTRATION.SHARED_TITLE');
});

const registrationModeDescription = computed(() => {
  return state.registrationMode === 'per_agent'
    ? t('INBOX_MGMT.ADD.VOICE.SIP_FORM.REGISTRATION.PER_AGENT_DESC')
    : t('INBOX_MGMT.ADD.VOICE.SIP_FORM.REGISTRATION.SHARED_DESC');
});

const retryValidation = () => {
  if (state.sipServer && state.sipTransport) {
    validateSipConnection({
      sipServer: state.sipServer,
      sipPort: state.sipPort || getDefaultSipPort(state.sipTransport),
      sipTransport: state.sipTransport,
    });
  }
};

// Watch para validar quando campos SIP mudarem (com debounce)
let debounceTimeout = null;
watch(
  [() => state.sipServer, () => state.sipPort, () => state.sipTransport],
  () => {
    // Limpar timeout anterior
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    // Criar novo timeout
    debounceTimeout = setTimeout(() => {
      if (state.sipServer && state.sipTransport) {
        console.log('[PLUGIN][SIP-CHANNEL] Validating SIP connection:', {
          server: state.sipServer,
          port: state.sipPort || '(default)',
          transport: state.sipTransport,
        });
        validateSipConnection({
          sipServer: state.sipServer,
          sipPort: state.sipPort || getDefaultSipPort(state.sipTransport),
          sipTransport: state.sipTransport,
        });
      }
    }, 1000);
  }
);

const transportOptions = [
  { label: 'WSS (WebSocket Secure - Recommended)', value: 'wss' },
  { label: 'WS (WebSocket)', value: 'ws' },
];

const validationRules = {
  name: { required },
  sipServer: { required, validSipServer },
  sipPort: { validPort }, // Porta opcional, usa padrão se vazio
};

const v$ = useVuelidate(validationRules, state);
const isSubmitDisabled = computed(() => {
  return v$.value.$invalid || validationStatus.value !== 'success';
});

const formErrors = computed(() => {
  const errors = {};

  if (v$.value.name?.$error) {
    errors.name = 'Inbox name is required';
  }

  if (v$.value.sipServer?.$error) {
    const serverErrors = v$.value.sipServer.$errors;
    if (serverErrors.find(e => e.$validator === 'required')) {
      errors.sipServer = 'SIP server is required';
    } else if (serverErrors.find(e => e.$validator === 'validSipServer')) {
      errors.sipServer = 'Must be a valid hostname or IP address';
    }
  }

  if (v$.value.sipPort?.$error) {
    const portErrors = v$.value.sipPort.$errors;
    if (portErrors.find(e => e.$validator === 'validPort')) {
      errors.sipPort = 'Port must be between 1 and 65535, or empty for default';
    }
  }

  return errors;
});

async function createChannel() {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  try {
    // Usar porta padrão se vazio
    const defaultPort =
      state.sipTransport === 'wss' ? DEFAULT_WSS_PORT : DEFAULT_WS_PORT;
    const port = state.sipPort ? parseInt(state.sipPort, 10) : defaultPort;

    console.log('[PLUGIN][SIP-CHANNEL] Creating SIP channel with config:', {
      name: state.name,
      sip: {
        sip_server: state.sipServer,
        sip_port: port,
        sip_transport: state.sipTransport,
        sip_realm: state.sipRealm || state.sipServer,
      },
    });

    const { data } = await SipChannelAPI.create({
      name: state.name,
      sip: {
        sip_server: state.sipServer,
        sip_port: port,
        sip_transport: state.sipTransport,
        sip_realm: state.sipRealm || state.sipServer,
      },
    });

    console.log(
      '[PLUGIN][SIP-CHANNEL] SIP channel created successfully:',
      data
    );

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: data.id },
    });
  } catch (error) {
    console.error('[PLUGIN][SIP-CHANNEL] SIP channel creation failed:', error);
    console.error('[PLUGIN][SIP-CHANNEL] Error response:', error.response);

    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.errors?.join(', ') ||
      'Failed to create SIP channel. Please check your configuration.';

    useAlert(errorMessage);
  }
}
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <PageHeader
      header-title="Voice (SIP)"
      header-content="Configure your SIP provider for voice calls. You can use any SIP-compliant provider."
    />

    <form
      class="flex flex-col gap-4 flex-wrap mx-0"
      @submit.prevent="createChannel"
    >
      <Input
        v-model="state.name"
        label="Inbox Name"
        placeholder="SIP Voice Inbox"
        :message="formErrors.name"
        :message-type="formErrors.name ? 'error' : 'info'"
        @blur="v$.name?.$touch"
      />

      <Input
        v-model="state.sipServer"
        label="SIP Server"
        placeholder="sip.example.com or 192.168.1.100"
        :message="formErrors.sipServer"
        :message-type="formErrors.sipServer ? 'error' : 'info'"
        @blur="v$.sipServer?.$touch"
      />

      <Input
        v-model="state.sipPort"
        label="SIP Port (optional)"
        placeholder="Leave empty for default (8089 for WSS, 8088 for WS)"
        :message="formErrors.sipPort"
        :message-type="formErrors.sipPort ? 'error' : 'info'"
        @blur="v$.sipPort?.$touch"
      />

      <Select
        v-model="state.sipTransport"
        name="sipTransport"
        label="Transport Protocol"
        placeholder="Select transport protocol"
        :options="transportOptions"
      />

      <Select
        v-model="state.registrationMode"
        name="registrationMode"
        label="Registration Mode"
        placeholder="Select registration mode"
        :options="registrationModeOptions"
      />

      <Input
        v-model="state.sipRealm"
        label="SIP Realm (optional)"
        placeholder="Leave empty to use server address"
      />

      <!-- Connection validation status -->
      <div
        v-if="isValidating || validationStatus"
        class="p-4 rounded-lg border"
        :class="{
          'bg-blue-50/30 border-blue-300/50': isValidating,
          'bg-green-50/40 border-green-300/50': validationStatus === 'success',
          'bg-red-50/30 border-red-300/50': validationStatus === 'error',
        }"
      >
        <div class="flex items-center justify-between gap-2">
          <div class="flex items-center gap-2">
            <span
              v-if="isValidating"
              class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"
            />
            <span
              v-else-if="validationStatus === 'success'"
              class="text-green-600"
            >
              {{ t('INBOX_MGMT.ADD.VOICE.SIP_FORM.VALIDATION.SUCCESS_ICON') }}
            </span>
            <span v-else-if="validationStatus === 'error'" class="text-red-600">
              {{ t('INBOX_MGMT.ADD.VOICE.SIP_FORM.VALIDATION.ERROR_ICON') }}
            </span>

            <span
              class="text-sm font-medium"
              :class="{
                'text-blue-700': isValidating,
                'text-green-700': validationStatus === 'success',
                'text-red-700': validationStatus === 'error',
              }"
            >
              {{ validationMessage }}
              <span
                v-if="formattedValidationTime"
                class="text-xs opacity-75 ml-1"
              >
                {{ validationTimeLabel }}
              </span>
            </span>
          </div>

          <NextButton
            v-if="!isValidating"
            variant="outline"
            size="sm"
            icon="arrow-clockwise"
            @click="retryValidation"
          >
            {{ t('INBOX_MGMT.ADD.VOICE.SIP_FORM.VALIDATION.RETRY') }}
          </NextButton>
        </div>
      </div>

      <div class="p-4 bg-gray-50 rounded-lg">
        <p class="text-sm text-gray-600 mb-2 font-medium">
          {{ registrationModeLabel }}
        </p>
        <p class="text-sm text-gray-600">
          {{ registrationModeDescription }}
        </p>
      </div>

      <div>
        <NextButton
          :is-loading="uiFlags.isCreating"
          :disabled="isSubmitDisabled"
          label="Create SIP Inbox"
          type="submit"
        />
      </div>
    </form>
  </div>
</template>
